// Screen 3b: Article detail - long-form guide + feedback + escalation

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@/ui';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getArticleById } from '../data/supportFaqData';
import MarkdownRenderer from './MarkdownRenderer';
import FeedbackWidget from './FeedbackWidget';
import EscalationSection from './EscalationSection';

interface ArticleDetailProps {
  articleId: string;
  onBack: () => void;
  onContactSupport: () => void;
  isAuthenticated?: boolean;
}

const COLORS = {
  accent: '#004b49',
  accentBg: '#e7f5f4',
  borderAccent: '#b8e3df',
  bgCard: '#ffffff',
  border: '#e5e7eb',
  text: '#111827',
  textSecondary: '#4b5563',
  textMuted: '#9ca3af',
  shadow: '0 1px 2px 0 rgba(0,0,0,0.04)',
};

function ArticleDetail({ articleId, onBack, onContactSupport, isAuthenticated = true }: ArticleDetailProps) {
  const escalationRef = useRef<HTMLDivElement>(null);
  const [showEscalation, setShowEscalation] = useState(false);

  const article = useMemo(() => getArticleById(articleId), [articleId]);

  useEffect(() => {
    setShowEscalation(false);
  }, [articleId]);

  useEffect(() => {
    if (showEscalation) {
      escalationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showEscalation]);

  if (!article) {
    return (
      <Box data-testid="features-support-faqs-components-article-detail-container-action-1" sx={{ py: 6, textAlign: 'center' }}>
        <Typography data-testid="features-support-faqs-components-article-detail-text-article-not-found." sx={{ fontSize: '14px', color: COLORS.textSecondary, fontFamily: 'Inter, sans-serif' }}>
          Article not found.
        </Typography>
      </Box>
    );
  }

  const handleNegativeFeedback = () => {
    setShowEscalation(true);
  };

  const handlePositiveFeedback = () => {
    setShowEscalation(false);
  };

  return (
    <Box data-testid="features-support-faqs-components-article-detail-container-action-2" sx={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Back button */}
      <Box data-testid="features-support-faqs-components-article-detail-container-on-back"
        component="button"
        onClick={onBack}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          mb: 2.5,
          px: 1,
          py: 0.75,
          ml: -1,
          borderRadius: '8px',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          color: COLORS.accent,
          fontSize: '13px',
          fontWeight: 500,
          fontFamily: 'Inter, sans-serif',
          '&:hover': { backgroundColor: COLORS.accentBg },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        Back
      </Box>

      {/* Type badge */}
      <Box data-testid="features-support-faqs-components-article-detail-container-action-3" sx={{ mb: 2 }}>
        <Box data-testid="features-support-faqs-components-article-detail-container-action-4"
          component="span"
          sx={{
            display: 'inline-block',
            fontSize: '11px',
            fontWeight: 500,
            px: 1.5,
            py: 0.5,
            borderRadius: '999px',
            backgroundColor: COLORS.accentBg,
            color: COLORS.accent,
            border: `1px solid ${COLORS.borderAccent}`,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {article.type}
        </Box>
      </Box>

      {/* Title */}
      <Typography data-testid="features-support-faqs-components-article-detail-text-action-1"
        sx={{
          fontSize: { xs: '22px', md: '26px' },
          fontWeight: 700,
          color: COLORS.text,
          lineHeight: 1.25,
          mb: 0.75,
          letterSpacing: '-0.02em',
          fontFamily: 'Inter, sans-serif',
        }}
      >
        {article.title}
      </Typography>
      <Typography data-testid="features-support-faqs-components-article-detail-text-action-2"
        sx={{ fontSize: '14px', color: COLORS.textSecondary, mb: 3, fontFamily: 'Inter, sans-serif' }}
      >
        {article.subtitle}
      </Typography>

      {/* Content */}
      <Box data-testid="features-support-faqs-components-article-detail-container-action-5"
        sx={{
          mb: 5,
          p: 3,
          borderRadius: '16px',
          backgroundColor: COLORS.bgCard,
          border: `1px solid ${COLORS.border}`,
          boxShadow: COLORS.shadow,
        }}
      >
        <MarkdownRenderer content={article.content} />
      </Box>

      {/* Feedback */}
      <FeedbackWidget
        onNegativeFeedback={handleNegativeFeedback}
        onPositiveFeedback={handlePositiveFeedback}
      />

      {/* Escalation — shown only after negative feedback */}
      {showEscalation && (
        <Box data-testid="features-support-faqs-components-article-detail-container-action-6" ref={escalationRef}>
          <EscalationSection onContactSupport={onContactSupport} isAuthenticated={isAuthenticated} />
        </Box>
      )}
    </Box>
  );
}

export default ArticleDetail;
